import {IntlShape} from 'react-intl'
import {Chart as ChartJS} from 'chart.js/auto'

import {cssTheme} from '@vio/atlas/src/themes'

import {CHART_COLORS, CHART_LAYOUT, LABEL_STYLES} from '../utils/constants'
import {priceTrendMessages} from '../utils/messages'

/**
 * Converts rem-based font sizes from the theme into px values for canvas.
 * Canvas behaves more predictably with absolute units.
 */
export const toPx = (size: string): string => {
  if (typeof size !== 'string') return size
  if (size.endsWith('rem')) {
    const remValue = parseFloat(size)
    // Atlas sets html font-size to 62.5%, so 1rem ~= 10px
    const basePx = 10
    const pixels = remValue * basePx
    return `${pixels}px`
  }
  return size
}

/**
 * Builds a canvas font string from theme typography values
 */
const buildCanvasFontString = (
  weight: number | string,
  size: string,
  family: string
) => `${weight} ${size} ${family}`

/**
 * Pre-computed font strings for canvas rendering (performance optimization)
 */
const CANVAS_FONTS = {
  bodyXS: buildCanvasFontString(
    cssTheme.typography.bodyXS.fontWeight,
    toPx(cssTheme.typography.bodyXS.fontSize),
    cssTheme.fonts.family.default
  ),
  labelXS: buildCanvasFontString(
    LABEL_STYLES.fontWeight,
    toPx(LABEL_STYLES.fontSize),
    cssTheme.fonts.family.default
  )
} as const

/**
 * Helper to select color based on dataset index
 */
const getDatasetColor = (
  datasetIndex: number,
  type: 'primary' | 'dot',
  primaryColor: string
): string => {
  const isPrimary = datasetIndex === 0
  if (type === 'dot') {
    return isPrimary ? primaryColor : CHART_COLORS.secondaryDot
  }
  return isPrimary ? primaryColor : CHART_COLORS.secondary
}

/**
 * Plugin to draw chart background color with rounded corners
 */
export const backgroundColorPlugin = {
  id: 'backgroundColor',
  beforeDraw(chart: ChartJS) {
    const {ctx, chartArea} = chart
    if (!chartArea) return

    ctx.save()
    ctx.fillStyle = CHART_COLORS.background
    ctx.beginPath()
    ctx.roundRect(
      chartArea.left,
      chartArea.top - CHART_LAYOUT.BACKGROUND_PADDING_Y,
      chartArea.right - chartArea.left,
      chartArea.bottom - chartArea.top + CHART_LAYOUT.BACKGROUND_PADDING_Y * 2,
      CHART_LAYOUT.BACKGROUND_RADIUS
    )
    ctx.fill()
    ctx.restore()
  }
}

/**
 * Creates a plugin to draw custom dots at the "Now" point
 * Note: Data is ordered most-recent-first, so index 0 is the newest point (rightmost)
 * @param primaryColor - Resolved brand color hex. Falls back to CHART_COLORS.primary.
 */
export const createCustomDotsPlugin = (
  primaryColor: string = CHART_COLORS.primary
) => ({
  id: 'customDots',
  afterDatasetsDraw(chart: ChartJS) {
    const {ctx, data} = chart

    data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex)
      if (!meta.data.length) return

      // Data is most-recent-first, so index 0 is the "Now" point
      const nowElement = meta.data[0]
      const color = getDatasetColor(datasetIndex, 'dot', primaryColor)

      // Offset the dot to the left to prevent overlap with the chart edge
      const dotX = nowElement.x - CHART_LAYOUT.DOT_OFFSET_X
      const dotY = nowElement.y

      ctx.save()

      // Draw outer spacing ring (2px transparent border using background color)
      const OUTER_RING_OFFSET = 2
      ctx.beginPath()
      ctx.arc(
        dotX,
        dotY,
        CHART_LAYOUT.DOT_RADIUS + OUTER_RING_OFFSET,
        0,
        Math.PI * 2
      )
      ctx.fillStyle = CHART_COLORS.background
      ctx.fill()

      // Draw main dot with white border
      ctx.beginPath()
      ctx.arc(dotX, dotY, CHART_LAYOUT.DOT_RADIUS, 0, Math.PI * 2)
      ctx.fillStyle = color
      ctx.fill()
      ctx.strokeStyle = CHART_COLORS.white
      ctx.lineWidth = CHART_LAYOUT.DOT_BORDER_WIDTH
      ctx.stroke()

      ctx.restore()
    })
  }
})

/**
 * Creates a plugin to draw a vertical dashed line at a specific day offset
 * Note: Data is ordered most-recent-first, so labels[0] is the "now" point
 */
export const createVerticalLinePlugin = (labels: string[]) => ({
  id: 'verticalLine',
  afterDraw(chart: ChartJS) {
    const {
      ctx,
      chartArea,
      scales: {x}
    } = chart
    if (!chartArea || !x || labels.length === 0) return

    const nowTimestamp = new Date(labels[0]).getTime()
    const targetTimestamp =
      nowTimestamp - CHART_LAYOUT.VERTICAL_LINE_DAY_OFFSET * 24 * 60 * 60 * 1000

    const xPos = x.getPixelForValue(targetTimestamp)

    // Only draw if the line falls within the chart area
    if (xPos < chartArea.left || xPos > chartArea.right) return

    ctx.save()
    ctx.beginPath()
    ctx.setLineDash(CHART_LAYOUT.VERTICAL_LINE_DASH)
    ctx.strokeStyle = CHART_COLORS.verticalLine
    ctx.lineWidth = 1
    ctx.moveTo(xPos, chartArea.top)
    ctx.lineTo(xPos, chartArea.bottom)
    ctx.stroke()
    ctx.restore()
  }
})

/**
 * Plugin to draw custom x-axis labels with day-interval ticks
 * Draws numeric ticks at 7-day intervals counting back from "Now",
 * a "Now" label at the newest point (rightmost), and a "days ago" subtitle.
 *
 * Note: Data is ordered most-recent-first:
 *   - labels[0] = newest timestamp = "Now" (rightmost)
 *   - labels[lastIndex] = oldest timestamp = "X days ago" (leftmost)
 */
export const createCustomXAxisLabelsPlugin = (
  labels: string[],
  formatMessage: IntlShape['formatMessage'],
  totalDaysOverride?: number
) => ({
  id: 'customXAxisLabels',
  afterDraw(chart: ChartJS) {
    const {
      ctx,
      chartArea,
      scales: {x}
    } = chart
    if (!chartArea || !x || labels.length === 0) return

    ctx.save()
    ctx.fillStyle = CHART_COLORS.text
    ctx.font = CANVAS_FONTS.bodyXS
    ctx.textBaseline = 'top'

    const yPos = chartArea.bottom + CHART_LAYOUT.X_AXIS_LABEL_OFFSET_Y

    const nowTimestamp = new Date(labels[0]).getTime()
    const totalDays = totalDaysOverride ?? CHART_LAYOUT.X_AXIS_TOTAL_DAYS
    const interval = CHART_LAYOUT.X_AXIS_TICK_INTERVAL

    // Generate tick positions at 7-day intervals up to the fixed total days
    // Always shows [7, 14, 21, 28] regardless of actual data range
    const tickDays: number[] = []
    for (let d = interval; d <= totalDays; d += interval) {
      tickDays.push(d)
    }

    // Draw numeric tick labels
    // The oldest tick (last in the array) is pinned to the left chart edge
    const lastTickIndex = tickDays.length - 1
    tickDays.forEach((days, index) => {
      const isOldestTick = index === lastTickIndex
      if (isOldestTick) {
        // Pin the oldest tick to the left chart edge
        ctx.textAlign = 'left'
        ctx.fillText(String(days), chartArea.left, yPos)
      } else {
        const tickTimestamp = nowTimestamp - days * 24 * 60 * 60 * 1000
        const tickXPos = x.getPixelForValue(tickTimestamp)
        ctx.textAlign = 'center'
        ctx.fillText(String(days), tickXPos, yPos)
      }
    })

    // Draw "Now" label at the rightmost (newest) point
    if (labels.length > 1) {
      const newestXPos = x.getPixelForValue(nowTimestamp)
      ctx.textAlign = 'right'
      ctx.fillText(
        formatMessage(priceTrendMessages.priceHistoryNow),
        newestXPos,
        yPos
      )
    }

    // Draw "X days ago" subtitle centered across the full chart width
    if (tickDays.length > 0) {
      const subtitleXPos = chart.width / 2

      ctx.fillStyle = CHART_COLORS.subtitleText
      ctx.textAlign = 'center'
      ctx.fillText(
        formatMessage(priceTrendMessages.priceHistoryDaysAgoLabel, {
          days: totalDays
        }),
        subtitleXPos,
        yPos + CHART_LAYOUT.X_AXIS_SUBTITLE_OFFSET_Y
      )
    }

    ctx.restore()
  }
})

/**
 * Minimum gap between labels to prevent overlap (in pixels)
 */
const LABEL_MIN_GAP = 4

/**
 * Creates a Chart.js plugin that renders static price labels at the "Now" point of each dataset
 * Note: Data is ordered most-recent-first, so index 0 is the newest point (rightmost)
 * @param formatPrice - A function that formats a numeric price value into a display string
 * @param primaryColor - Resolved brand color hex for the primary label background. Falls back to CHART_COLORS.primary.
 */
export const createStaticLabelsPlugin = (
  formatPrice: (value: number) => string,
  primaryColor: string = CHART_COLORS.primary
) => ({
  id: 'staticLabels',
  afterDatasetsDraw(chart: ChartJS) {
    const {ctx, data} = chart

    const labelHeight = CHART_LAYOUT.LABEL_HEIGHT

    // First pass: collect all label data
    const labelsData: {
      datasetIndex: number
      nowElement: {x: number; y: number}
      value: number
      text: string
      textWidth: number
      labelWidth: number
      labelY: number // Initial Y position (centered on data point)
    }[] = []

    data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex)
      if (!meta.data.length) return

      const nowElement = meta.data[0]
      const value = dataset.data[0] as number
      const text = formatPrice(value)

      ctx.font = CANVAS_FONTS.labelXS
      const textWidth = ctx.measureText(text).width
      const labelWidth = textWidth + LABEL_STYLES.paddingX * 2

      labelsData.push({
        datasetIndex,
        nowElement: {x: nowElement.x, y: nowElement.y},
        value,
        text,
        textWidth,
        labelWidth,
        labelY: nowElement.y - labelHeight / 2
      })
    })

    // Second pass: adjust positions to prevent overlap
    // Sort by Y position to handle overlap detection
    labelsData.sort((a, b) => a.labelY - b.labelY)

    for (let i = 1; i < labelsData.length; i++) {
      const prevLabel = labelsData[i - 1]
      const currLabel = labelsData[i]

      const prevBottom = prevLabel.labelY + labelHeight
      const currTop = currLabel.labelY
      const overlap = prevBottom + LABEL_MIN_GAP - currTop

      if (overlap > 0) {
        // Labels overlap - spread them apart equally
        const adjustment = overlap / 2
        prevLabel.labelY -= adjustment
        currLabel.labelY += adjustment
      }
    }

    // Third pass: render all labels
    labelsData.forEach(labelData => {
      const {datasetIndex, nowElement, text, labelWidth, labelY} = labelData
      const isPrimary = datasetIndex === 0
      const styles = isPrimary
        ? {...LABEL_STYLES.primary, background: primaryColor}
        : LABEL_STYLES.secondary

      // Position label to the LEFT of the "Now" dot (which is always on the right)
      const labelX = nowElement.x - labelWidth - LABEL_STYLES.offsetX

      ctx.save()

      // Apply shadow settings
      ctx.shadowColor = CHART_LAYOUT.LABEL_SHADOW.color
      ctx.shadowBlur = CHART_LAYOUT.LABEL_SHADOW.blur
      ctx.shadowOffsetX = CHART_LAYOUT.LABEL_SHADOW.offsetX
      ctx.shadowOffsetY = CHART_LAYOUT.LABEL_SHADOW.offsetY

      // Draw background with rounded corners
      ctx.fillStyle = styles.background
      ctx.beginPath()
      ctx.roundRect(
        labelX,
        labelY,
        labelWidth,
        labelHeight,
        LABEL_STYLES.borderRadius
      )
      ctx.fill()

      // Reset shadow for text (no shadow on text itself)
      ctx.shadowColor = 'transparent'

      // Draw text
      ctx.fillStyle = styles.text
      ctx.textAlign = 'left'
      ctx.textBaseline = 'middle'
      ctx.fillText(
        text,
        labelX + LABEL_STYLES.paddingX,
        labelY + labelHeight / 2
      )

      ctx.restore()
    })
  }
})
